/**
 * CustomerDrawer — Slide-in panel showing customer details from the new customers table.
 * Opens when clicking a contact name in the Résumé tab (admin only).
 * Shows customer profile, linked dossiers, and allows editing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, PencilIcon, CheckIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { apiClient } from '@/core/api/client';

interface CustomerData {
  id: string;
  type: 'person' | 'company';
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  vat_number: string | null;
  email: string | null;
  phone: string | null;
  phone_mobile: string | null;
  address_street: string | null;
  address_number: string | null;
  address_postcode: string | null;
  address_city: string | null;
  address_country: string;
  language: string;
  fm_contact_id: number | null;
  created_at: string;
}

interface CustomerDossier {
  id: number;
  status: string;
  date_demande: string | null;
  property_street: string | null;
  property_city: string | null;
  property_postcode: string | null;
  role: string;
}

interface CustomerDrawerProps {
  fmContactId: number | null;
  contactName: string;
  onClose: () => void;
}

export const CustomerDrawer: React.FC<CustomerDrawerProps> = ({
  fmContactId,
  contactName,
  onClose,
}) => {
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [dossiers, setDossiers] = useState<CustomerDossier[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<CustomerData>>({});
  const [saving, setSaving] = useState(false);

  const fetchCustomer = useCallback(async () => {
    if (!fmContactId) {
      setLoading(false);
      return;
    }
    try {
      // Search customer by fm_contact_id via the search endpoint
      const searchRes: any = await apiClient.get(`/api/customers/search`, {
        params: { q: String(fmContactId), limit: 1 }
      });

      if (searchRes.success && searchRes.data.length > 0) {
        const c = searchRes.data[0];
        setCustomer(c);

        // Fetch dossiers
        const dossiersRes: any = await apiClient.get(`/api/customers/${c.id}/dossiers`);
        if (dossiersRes.success) {
          setDossiers(dossiersRes.data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch customer:', err);
    } finally {
      setLoading(false);
    }
  }, [fmContactId]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  const handleEdit = () => {
    if (!customer) return;
    setEditData({
      first_name: customer.first_name,
      last_name: customer.last_name,
      company_name: customer.company_name,
      email: customer.email,
      phone: customer.phone,
      phone_mobile: customer.phone_mobile,
      address_street: customer.address_street,
      address_postcode: customer.address_postcode,
      address_city: customer.address_city,
    });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!customer) return;
    setSaving(true);
    try {
      const res: any = await apiClient.put(`/api/customers/${customer.id}`, editData);
      if (res.success) {
        setCustomer(res.data);
        setEditing(false);
      }
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const displayName = customer
    ? customer.type === 'company'
      ? customer.company_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
      : `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
    : contactName;

  return (
    <>
      {/* Backdrop */}
      <div className="customer-drawer-backdrop" onClick={onClose} />

      {/* Drawer */}
      <div className="customer-drawer">
        {/* Header */}
        <div className="customer-drawer-header">
          <div>
            <h2 className="customer-drawer-title">{displayName || 'Client'}</h2>
            {customer?.type === 'company' && (
              <span className="customer-drawer-badge">Société</span>
            )}
          </div>
          <button className="customer-drawer-close" onClick={onClose}>
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="customer-drawer-body">
          {loading ? (
            <div className="customer-drawer-loading">
              <div className="aff-detail-spinner" />
              <span>Recherche du client...</span>
            </div>
          ) : !customer ? (
            <div className="customer-drawer-empty">
              <p>Ce contact n'a pas encore de fiche client.</p>
              <p className="text-gray-400 text-xs mt-1">fm_contact_id: {fmContactId || 'N/A'}</p>
            </div>
          ) : (
            <>
              {/* Profile section */}
              <div className="customer-drawer-section">
                <div className="customer-drawer-section-header">
                  <h3>Coordonnées</h3>
                  {!editing ? (
                    <button className="customer-drawer-edit-btn" onClick={handleEdit}>
                      <PencilIcon className="w-3.5 h-3.5" />
                      Modifier
                    </button>
                  ) : (
                    <div className="customer-drawer-edit-actions">
                      <button className="customer-drawer-save-btn" onClick={handleSave} disabled={saving}>
                        <CheckIcon className="w-3.5 h-3.5" />
                        {saving ? '...' : 'Sauver'}
                      </button>
                      <button className="customer-drawer-cancel-btn" onClick={() => setEditing(false)}>
                        <XCircleIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                {editing ? (
                  <div className="customer-drawer-form">
                    <div className="customer-drawer-form-row">
                      <label>Prénom</label>
                      <input value={editData.first_name || ''} onChange={e => setEditData(d => ({ ...d, first_name: e.target.value }))} />
                    </div>
                    <div className="customer-drawer-form-row">
                      <label>Nom</label>
                      <input value={editData.last_name || ''} onChange={e => setEditData(d => ({ ...d, last_name: e.target.value }))} />
                    </div>
                    <div className="customer-drawer-form-row">
                      <label>Email</label>
                      <input type="email" value={editData.email || ''} onChange={e => setEditData(d => ({ ...d, email: e.target.value }))} />
                    </div>
                    <div className="customer-drawer-form-row">
                      <label>Tél.</label>
                      <input value={editData.phone || ''} onChange={e => setEditData(d => ({ ...d, phone: e.target.value }))} />
                    </div>
                    <div className="customer-drawer-form-row">
                      <label>Mobile</label>
                      <input value={editData.phone_mobile || ''} onChange={e => setEditData(d => ({ ...d, phone_mobile: e.target.value }))} />
                    </div>
                    <div className="customer-drawer-form-row">
                      <label>Rue</label>
                      <input value={editData.address_street || ''} onChange={e => setEditData(d => ({ ...d, address_street: e.target.value }))} />
                    </div>
                    <div className="customer-drawer-form-row customer-drawer-form-row-split">
                      <div>
                        <label>CP</label>
                        <input value={editData.address_postcode || ''} onChange={e => setEditData(d => ({ ...d, address_postcode: e.target.value }))} />
                      </div>
                      <div>
                        <label>Ville</label>
                        <input value={editData.address_city || ''} onChange={e => setEditData(d => ({ ...d, address_city: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="customer-drawer-fields">
                    <Field label="Email" value={customer.email} />
                    <Field label="Tél." value={customer.phone} />
                    <Field label="Mobile" value={customer.phone_mobile} />
                    {customer.vat_number && <Field label="TVA" value={customer.vat_number} />}
                    <Field
                      label="Adresse"
                      value={[
                        [customer.address_number, customer.address_street].filter(Boolean).join(' '),
                        [customer.address_postcode, customer.address_city].filter(Boolean).join(' ')
                      ].filter(Boolean).join(', ') || null}
                    />
                    <Field label="Langue" value={customer.language?.toUpperCase()} />
                  </div>
                )}
              </div>

              {/* Dossiers section */}
              <div className="customer-drawer-section">
                <div className="customer-drawer-section-header">
                  <h3>Dossiers ({dossiers.length})</h3>
                </div>
                {dossiers.length === 0 ? (
                  <p className="customer-drawer-empty-text">Aucun dossier lié</p>
                ) : (
                  <div className="customer-drawer-dossiers">
                    {dossiers.map(d => (
                      <a
                        key={d.id}
                        href={`/admin/affaire/${d.id}`}
                        className="customer-drawer-dossier"
                        onClick={(e) => {
                          // Ctrl+click opens new tab, normal click navigates
                          if (!e.ctrlKey && !e.metaKey) {
                            e.preventDefault();
                            window.location.href = `/admin/affaire/${d.id}`;
                          }
                        }}
                      >
                        <div className="customer-drawer-dossier-id">#{d.id}</div>
                        <div className="customer-drawer-dossier-info">
                          <span className="customer-drawer-dossier-address">
                            {[d.property_street, d.property_postcode, d.property_city].filter(Boolean).join(', ') || 'Adresse non renseignée'}
                          </span>
                          <span className="customer-drawer-dossier-status">{d.status}</span>
                        </div>
                        <div className="customer-drawer-dossier-role">{d.role}</div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const Field: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => (
  <div className="customer-drawer-field">
    <span className="customer-drawer-field-label">{label}</span>
    <span className="customer-drawer-field-value">{value || '-'}</span>
  </div>
);
